import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, CONTACT_INFO, features, BRAND_INFO, COUNTRY_OF_ORIGIN } from '../data/mock';
import { ArrowRight, CheckCircle, Award, Users, Heart, Target, Sparkles, MapPin, Shield } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const AboutPage = () => {
  usePageTitle('About Us', 'Learn about Stitch Your Dash - your trusted source for smart sewing tools and craft accessories for makers of all skill levels.');
  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Hero Banner */}
        <section className="relative bg-warmBrown-800 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={siteImages.aboutBanner}
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-900 via-warmBrown-900/95 to-warmBrown-800/80" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-20" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full text-rose-300 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                About Us
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4">
                Your Sewing Journey Starts Here
              </h1>
              <p className="text-warmBrown-300 text-lg">Empowering crafters with quality tools and accessories.</p>
            </div>
          </div>
        </section>

        {/* Company Description */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-rose-100 text-rose-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">Our Mission</span>
                <h2 className="font-display text-3xl lg:text-4xl text-warmBrown-900 mb-6">
                  Empowering Crafters Through Quality Tools
                </h2>
                <p className="text-warmBrown-600 leading-relaxed mb-6">
                  At {BRAND_INFO.name}, we specialize in providing high-quality <strong>sewing tools, craft accessories, and DIY equipment</strong> for makers of all skill levels. Whether you're a beginner learning to sew or a professional quilter, we have the tools you need to bring your creative visions to life.
                </p>
                <p className="text-warmBrown-600 leading-relaxed mb-6">
                  Our product catalog includes: <strong>sewing machines, rotary cutters, fabric scissors, marking tools, dress forms, sewing tables, LED lights, bobbins, clips,</strong> and many more essential crafting supplies.
                </p>
                <p className="text-warmBrown-600 leading-relaxed mb-8">
                  We carefully curate our product selection, partnering only with trusted manufacturers who share our commitment to quality and value. Every product in our catalog is chosen to help you on your creative journey.
                </p>

                {/* Business Info */}
                <div className="bg-cream-100 rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin className="w-5 h-5 text-rose-500" />
                    <span className="font-semibold text-warmBrown-900">Based in the {COUNTRY_OF_ORIGIN}</span>
                  </div>
                  <p className="text-warmBrown-600 text-sm">
                    {BRAND_INFO.name} is proudly operated by <strong>{CONTACT_INFO.corporateName}</strong>, located at {CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}.
                  </p>
                </div>

                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 text-rose-600 font-semibold hover:text-rose-700 transition-colors group"
                >
                  Browse Our Products
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              <div className="relative">
                <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-elevated">
                  <img
                    src={siteImages.heroWorkspace}
                    alt="Our Mission"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-rose-500 text-white p-6 rounded-2xl shadow-lg hidden md:block">
                  <p className="text-4xl font-bold">11+</p>
                  <p className="text-rose-100 text-sm">Quality Products</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Products & Services */}
        <section className="py-16 lg:py-24 bg-cream-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-rose-100 text-rose-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">What We Offer</span>
              <h2 className="font-display text-3xl lg:text-4xl text-warmBrown-900">Our Products & Services</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-8 rounded-3xl shadow-card border border-cream-200">
                <h3 className="font-semibold text-warmBrown-900 text-lg mb-3">Sewing Machines & Tables</h3>
                <p className="text-warmBrown-600 text-sm">Portable sewing machines and sturdy sewing tables for your workspace.</p>
              </div>
              <div className="bg-white p-8 rounded-3xl shadow-card border border-cream-200">
                <h3 className="font-semibold text-warmBrown-900 text-lg mb-3">Cutting Tools</h3>
                <p className="text-warmBrown-600 text-sm">Rotary cutters, fabric scissors, and electric cutters for precise cuts.</p>
              </div>
              <div className="bg-white p-8 rounded-3xl shadow-card border border-cream-200">
                <h3 className="font-semibold text-warmBrown-900 text-lg mb-3">Marking & Measuring</h3>
                <p className="text-warmBrown-600 text-sm">Heat erasable pens, seam guides, and measuring tools.</p>
              </div>
              <div className="bg-white p-8 rounded-3xl shadow-card border border-cream-200">
                <h3 className="font-semibold text-warmBrown-900 text-lg mb-3">Dress Forms</h3>
                <p className="text-warmBrown-600 text-sm">Adjustable professional dress form mannequins for garment fitting.</p>
              </div>
              <div className="bg-white p-8 rounded-3xl shadow-card border border-cream-200">
                <h3 className="font-semibold text-warmBrown-900 text-lg mb-3">Accessories</h3>
                <p className="text-warmBrown-600 text-sm">Sewing clips, bobbin winders, LED lights, and more.</p>
              </div>
              <div className="bg-white p-8 rounded-3xl shadow-card border border-cream-200">
                <h3 className="font-semibold text-warmBrown-900 text-lg mb-3">Fast USA Shipping</h3>
                <p className="text-warmBrown-600 text-sm">Free shipping on orders $99+ via USPS, delivered within the USA only.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 lg:py-24 bg-rose-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-14">
              <h2 className="font-display text-3xl lg:text-4xl text-white">Our Values</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl text-center hover:bg-white/20 transition-colors">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">Quality First</h3>
                <p className="text-rose-100 text-sm">Premium materials and rigorous testing for every product.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl text-center hover:bg-white/20 transition-colors">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">Customer Focus</h3>
                <p className="text-rose-100 text-sm">Your crafting success is our top priority.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl text-center hover:bg-white/20 transition-colors">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">Integrity</h3>
                <p className="text-rose-100 text-sm">Honest recommendations and transparent practices.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl text-center hover:bg-white/20 transition-colors">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">Accessibility</h3>
                <p className="text-rose-100 text-sm">Affordable crafting solutions for everyone.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-14">
              <span className="inline-block px-3 py-1 bg-sage-100 text-sage-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">Why Choose Us</span>
              <h2 className="font-display text-3xl lg:text-4xl text-warmBrown-900">
                The {BRAND_INFO.name} Promise
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => (
                <div key={feature.id} className="text-center p-8 bg-cream-50 rounded-3xl hover:shadow-card transition-shadow border border-cream-200">
                  <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-rose-500" />
                  </div>
                  <h3 className="font-semibold text-warmBrown-800 mb-2">{feature.title}</h3>
                  <p className="text-warmBrown-500 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 lg:py-24 bg-warmBrown-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl lg:text-4xl text-white mb-6">
              Have Questions?
            </h2>
            <p className="text-warmBrown-300 mb-10 text-lg">
              Our team is here to help you find the right tools for your sewing projects.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="px-8 py-4 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-2xl transition-colors shadow-lg"
              >
                Contact Us
              </Link>
              <a
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-2xl transition-colors"
              >
                Call {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
